/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import PropTypes from 'prop-types';
import { FlexGrid, Dropdown } from '@gasbuddy/react-components';
import Modal from '../../modal';
import InputWithLabel from '../../input-with-header';
import Notification from '../../notification';

import sharedStyles from '../../../styles/shared.css';
import styles from './styles.css';

const { api, logError } = require('../../../../lib/bputils').default;

const DEFAULT_TEMPLATE = { id: 0, category: '', title: '', message: '' };

export default function AddEditTemplateModal({ categories, template: templateProps, className, onTemplateChanged, onModalOpen, onModalClose }) {
  const [template, setTemplate] = React.useState(templateProps);
  const [hasTemplateTitleError, setHasTemplateTitleError] = React.useState(false);
  const [hasTemplateMessageError, setHasTemplateMessageError] = React.useState(false);

  const addReplyTemplate = async (temp) => {
    try {
      await api.post({
        path: '/reviews/replies/templates',
        data: {
          category: temp.category,
          title: temp.title,
          message: temp.message,
        },
        requestId: 'createReplyTemplate',
      });

      Notification.success('The Reply Template was created successfully');
    } catch (err) {
      logError(err);
      Notification.error(err.message);
    }
  };

  const updateReplyTemplate = async (temp) => {
    try {
      await api.put({
        path: `/reviews/replies/templates/${temp.id}`,
        data: {
          category: temp.category,
          title: temp.title,
          message: temp.message,
        },
        requestId: 'updateReplyTemplate',
      });

      Notification.success('The Reply Template was updated successfully');
    } catch (err) {
      logError(err);
      Notification.error('There was an error updating the Reply Template.');
    }
  };

  const deleteReplyTemplate = async (templateId) => {
    try {
      await api.delete({
        path: `/reviews/replies/templates/${templateId}`,
        requestId: 'deleteReplyTemplate',
      });

      Notification.success('The Reply Template was deleted successfully');
    } catch (err) {
      logError(err);
      Notification.error('There was an error deleting the Reply Template.');
    }
  };

  const onChangeCategory = (value) => {
    setTemplate({ ...template, category: value });
  };

  const handleOnTitleInputChange = (value) => {
    setTemplate({ ...template, title: value });
    if (hasTemplateTitleError) {
      setHasTemplateTitleError(false);
    }
  };

  const handleOnMessageInputChange = (value) => {
    setTemplate({ ...template, message: value });
    if (hasTemplateMessageError) {
      setHasTemplateMessageError(false);
    }
  };

  const handleOnSaveTemplate = async () => {
    if (template.title.length && template.message.length) {
      if (template.id) {
        await updateReplyTemplate(template);
      } else {
        await addReplyTemplate(template);
      }
      onTemplateChanged(template);
      setTemplate(DEFAULT_TEMPLATE);
      return true;
    }
    if (!template.title.length) {
      setHasTemplateTitleError(true);
    }
    if (!template.message.length) {
      setHasTemplateMessageError(true);
    }
    return false;
  };

  const handleOnDeleteTemplate = async () => {
    const { id: templateId } = template;
    await deleteReplyTemplate(templateId);
    onTemplateChanged(templateId);
  };

  const handleOnClickModalOpenerButton = (e) => {
    e.preventDefault();
    onModalOpen();
  };

  const validRequiredInput = (value, errorSetter) => {
    errorSetter(!value.length);
  };

  const onClose = () => {
    setHasTemplateMessageError(false);
    setHasTemplateTitleError(false);
    onModalClose();
  };

  return (
    <Modal
      title={template.id ? 'EDIT REPLY TEMPLATE' : 'ADD REPLY TEMPLATE'}
      className={className}
      saveButtonText={template.id ? 'UPDATE TEMPLATE' : 'SAVE TEMPLATE'}
      deleteButtonText={template.id ? 'DELETE' : null}
      onSave={handleOnSaveTemplate}
      onDelete={handleOnDeleteTemplate}
      onClose={onClose}
      size="sm"
      button={(
        <div className={template.id ? styles.addTemplateButton : ''}>
          <a
            className={sharedStyles.linkButton}
            onClick={handleOnClickModalOpenerButton}
          >
            {template.id ? 'EDIT' : 'ADD REPLY TEMPLATE'}
          </a>
        </div>
      )}
    >
      <FlexGrid container className={styles.replyTemplateModal}>
        <FlexGrid.Column desktop={6} mobile={12} tablet={6} className={styles.categoryDropdownContainer}>
          <div className={styles.categoryDropdownLabel}>
              Category
          </div>
          <Dropdown
            className={styles.brandDropdown}
            onChange={e => onChangeCategory(e.currentTarget.value)}
            options={
                categories.map(categoryName => ({
                  value: categoryName,
                  label: categoryName,
                }))
              }
            value={template.category}
          />
        </FlexGrid.Column>
        <FlexGrid.Column desktop={6} mobile={12} tablet={6}>
          <InputWithLabel
            label="Title"
            onChange={e => handleOnTitleInputChange(e.currentTarget.value)}
            editable
            value={template.title}
            maxLength={40}
            required
            onBlur={e => validRequiredInput(e.currentTarget.value, setHasTemplateTitleError)}
            error={hasTemplateTitleError ? 'Please set a template title' : null}
            isInvalid={hasTemplateTitleError}
          />
        </FlexGrid.Column>
        <FlexGrid.Column desktop={12} mobile={12} tablet={12}>
          <InputWithLabel
            label="Message"
            onChange={e => handleOnMessageInputChange(e.currentTarget.value)}
            editable
            value={template.message}
            textArea
            required
            onBlur={e => validRequiredInput(e.currentTarget.value, setHasTemplateMessageError)}
            error={hasTemplateMessageError ? 'Please set a template message' : null}
            isInvalid={hasTemplateMessageError}
          />
        </FlexGrid.Column>
      </FlexGrid>
    </Modal>
  );
}

AddEditTemplateModal.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  template: PropTypes.shape({
    id: PropTypes.number,
    category: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
  }),
  className: PropTypes.string,
  onTemplateChanged: PropTypes.func,
  onModalOpen: PropTypes.func,
  onModalClose: PropTypes.func,
};

AddEditTemplateModal.defaultProps = {
  className: '',
  template: DEFAULT_TEMPLATE,
  onTemplateChanged: () => {},
  onModalOpen: null,
  onModalClose: null,
};
